import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Button,
  Container,
  Chip,
  Stack,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Badge,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import * as actions from '../../redux/actions';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import EmployeeAvatar from '../../components/EmployeeAvatar';

const STATUS_CONFIG = {
  PENDING:  { label: 'รออนุมัติ',  color: 'warning' },
  APPROVED: { label: 'อนุมัติแล้ว', color: 'success' },
  REJECTED: { label: 'ปฏิเสธ',     color: 'error'   },
};

export default function HrTimestampApproval({ title }) {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);

  const [tab, setTab] = useState('PENDING');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // reject dialog
  const [rejectTarget, setRejectTarget]   = useState(null);
  const [rejectNote, setRejectNote]       = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // photo preview
  const [photoSrc, setPhotoSrc] = useState(null);

  const fetchRows = useCallback(async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/timestamp?approval_status=${status}&out_of_location=true&size=999`,
      );
      setRows(data?.rows || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchRows(tab); }, [tab]);

  const socket = useSocket();
  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchRows(tab);
    socket.on('timestamp:created', refresh);
    socket.on('timestamp:updated', refresh);
    return () => {
      socket.off('timestamp:created', refresh);
      socket.off('timestamp:updated', refresh);
    };
  }, [socket, tab, fetchRows]);

  const handleApprove = async (row) => {
    if (!(await confirmAsync(`อนุมัติคำขอลงเวลาของ ${formatEmployeeThaiName({
      title: row.employee_title,
      firstname_th: row.employee_firstname_th,
      lastname_th: row.employee_lastname_th,
      firstname: row.employee_firstname,
      lastname: row.employee_lastname,
    })}?`))) return;
    setActionLoading(true);
    try {
      await dispatch(actions.timestampApprove(row._id, {
        status: 'APPROVED',
        approved_by: formatEmployeeThaiName(me?.userData),
      }));
      fetchRows(tab);
    } catch { showAlert('เกิดข้อผิดพลาด กรุณาลองใหม่'); }
    finally { setActionLoading(false); }
  };

  const openRejectDialog = (row) => { setRejectTarget(row); setRejectNote(''); };
  const closeRejectDialog = () => { setRejectTarget(null); setRejectNote(''); };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionLoading(true);
    try {
      await dispatch(actions.timestampApprove(rejectTarget._id, {
        status: 'REJECTED',
        approval_note: rejectNote,
        approved_by: formatEmployeeThaiName(me?.userData),
      }));
      closeRejectDialog();
      fetchRows(tab);
    } catch { showAlert('เกิดข้อผิดพลาด กรุณาลองใหม่'); }
    finally { setActionLoading(false); }
  };

  const pendingCount = tab === 'PENDING' ? rows.length : null;

  const renderPhoto = (row) => {
    const url = row.image?.url;
    if (!url) return <Typography variant="caption" color="text.disabled">ไม่มีรูป</Typography>;
    return (
      <Tooltip title="คลิกดูรูปขนาดใหญ่">
        <Box
          component="img"
          src={url}
          alt="checkin"
          onClick={() => setPhotoSrc(url)}
          sx={{
            width: 52,
            height: 52,
            objectFit: 'cover',
            borderRadius: 1.5,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: 'divider',
            '&:hover': { borderColor: 'primary.main', opacity: 0.85 },
          }}
        />
      </Tooltip>
    );
  };

  const renderStatusChip = (row) => {
    const cfg = STATUS_CONFIG[row.approval_status] || STATUS_CONFIG.PENDING;
    return <Chip label={cfg.label} color={cfg.color} size="small" />;
  };

  const renderSkeletonRows = () =>
    Array.from({ length: 4 }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: 6 }).map((__, j) => (
          <TableCell key={j}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ));

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: 2,
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <LocationOffIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>{title || 'คำขอลงเวลานอกพื้นที่'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ตรวจสอบและอนุมัติคำขอลงเวลาของพนักงานที่อยู่นอกเขตพื้นที่ปฏิบัติงาน
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => fetchRows(tab)} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 1 }}
        >
          <Tab
            value="PENDING"
            label={
              <Badge badgeContent={tab === 'PENDING' ? rows.length : undefined} color="error" max={99}>
                <Typography variant="body2" fontWeight={tab === 'PENDING' ? 700 : 400} sx={{ pr: rows.length > 0 && tab === 'PENDING' ? 1.5 : 0 }}>
                  รออนุมัติ
                </Typography>
              </Badge>
            }
          />
          <Tab value="APPROVED" label="อนุมัติแล้ว" />
          <Tab value="REJECTED" label="ปฏิเสธ" />
        </Tabs>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700, width: 64 }}>รูป</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>พนักงาน</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>โครงการ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>เวลาที่ขอ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>เหตุผล</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>GPS</TableCell>
                {tab !== 'PENDING' && (
                  <TableCell sx={{ fontWeight: 700 }}>
                    {tab === 'APPROVED' ? 'ผู้อนุมัติ' : 'ผู้ปฏิเสธ'}
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  {tab === 'PENDING' ? 'ดำเนินการ' : 'สถานะ'}
                </TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? renderSkeletonRows()
                : rows.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary" variant="body2">ไม่มีรายการ</Typography>
                    </TableCell>
                  </TableRow>
                )
                : rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{renderPhoto(row)}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <EmployeeAvatar
                          src={row.employee_image_url || row.employee_profile_image_url}
                          size={30}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {formatEmployeeThaiName({
                              title: row.employee_title,
                              firstname_th: row.employee_firstname_th,
                              lastname_th: row.employee_lastname_th,
                              firstname: row.employee_firstname,
                              lastname: row.employee_lastname,
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.employee?.employee_id || ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.project_in?.project_number
                          ? `${row.project_in.project_number} | `
                          : ''}
                        {row.project_in?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {dayjs(row.checkIn).format('DD/MM/YY HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {row.note || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.locationCheckIn?.currentLocation?.lat != null ? (
                        <Tooltip title="เปิดใน Google Maps">
                          <Box
                            component="a"
                            href={`https://www.google.com/maps?q=${row.locationCheckIn.currentLocation.lat},${row.locationCheckIn.currentLocation.lon}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                          >
                            <MyLocationIcon sx={{ fontSize: 14 }} />
                            <Box>
                              <Typography variant="caption" display="block" sx={{ lineHeight: 1.2 }}>
                                {row.locationCheckIn.currentLocation.lat.toFixed(5)}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ lineHeight: 1.2 }}>
                                {row.locationCheckIn.currentLocation.lon.toFixed(5)}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">ไม่มี GPS</Typography>
                      )}
                    </TableCell>
                    {tab !== 'PENDING' && (
                      <TableCell>
                        {row.approved_by ? (
                          <>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {tab === 'APPROVED' ? 'อนุมัติโดย' : 'ปฏิเสธโดย'}:{' '}
                              <strong>{row.approved_by}</strong>
                            </Typography>
                            {row.approved_at && (
                              <Typography variant="caption" display="block" color="text.disabled">
                                {dayjs(row.approved_at).format('DD/MM/YY HH:mm')}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" color="text.disabled">-</Typography>
                        )}
                        {row.approval_note && (
                          <Typography variant="caption" display="block" color="error.main" sx={{ mt: 0.5 }}>
                            หมายเหตุ: {row.approval_note}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {tab === 'PENDING' ? (
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApprove(row)}
                            disabled={actionLoading}
                            sx={{ minWidth: 80 }}
                          >
                            อนุมัติ
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => openRejectDialog(row)}
                            disabled={actionLoading}
                            sx={{ minWidth: 80 }}
                          >
                            ปฏิเสธ
                          </Button>
                        </Stack>
                      ) : (
                        renderStatusChip(row)
                      )}
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={!!rejectTarget} onClose={closeRejectDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>ปฏิเสธคำขอลงเวลา</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            พนักงาน: <strong>{formatEmployeeThaiName({
              title: rejectTarget?.employee_title,
              firstname_th: rejectTarget?.employee_firstname_th,
              lastname_th: rejectTarget?.employee_lastname_th,
              firstname: rejectTarget?.employee_firstname,
              lastname: rejectTarget?.employee_lastname,
            })}</strong>
          </Typography>
          <TextField
            fullWidth
            label="เหตุผลที่ปฏิเสธ (ถ้ามี)"
            multiline
            rows={3}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRejectDialog} disabled={actionLoading}>ยกเลิก</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
          >
            {actionLoading ? 'กำลังบันทึก...' : 'ยืนยันปฏิเสธ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Preview Dialog */}
      <Dialog open={!!photoSrc} onClose={() => setPhotoSrc(null)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 1, textAlign: 'center' }}>
          {photoSrc && (
            <Box
              component="img"
              src={photoSrc}
              alt="checkin-preview"
              sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoSrc(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
